import { useEffect, useMemo, useState, useCallback } from "react";
import JobLink from "@/components/JobLink";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { downloadCSV, inr, Kpi, EmptyRow } from "./utils";

export default function JobwiseRmPctReport() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [flag, setFlag] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    setBusy(true);
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/reports/jobs/rm-percentage", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    let list = data.rows;
    if (flag !== "all") list = list.filter((r) => r.flag === flag);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((r) =>
        (r.mpwo_number || "").toLowerCase().includes(n) ||
        (r.customer_name || "").toLowerCase().includes(n) ||
        (r.product_name || "").toLowerCase().includes(n) ||
        (r.substrate_code || "").toLowerCase().includes(n)
      );
    }
    return list;
  }, [data, flag, q]);

  const totals = data?.totals || {};

  const exportCSV = () => downloadCSV(
    "jobwise-rm-percentage",
    ["MPWO", "Job Number", "Customer", "Product", "Substrate", "Quantity", "Revenue", "Material Cost", "RM %", "Flag", "Status", "Created"],
    rows.map((r) => [
      r.mpwo_number, r.job_number, r.customer_name, r.product_name, r.substrate_code,
      r.quantity, r.revenue, r.material_cost, r.rm_pct, r.flag, r.status,
      (r.created_at || "").slice(0, 10),
    ])
  );

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">From date</label>
          <Input data-testid="rm-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm w-40" />
        </div>
        <div>
          <label className="label-caps block mb-1">To date</label>
          <Input data-testid="rm-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm w-40" />
        </div>
        <div>
          <label className="label-caps block mb-1">Flag</label>
          <Select value={flag} onValueChange={setFlag}>
            <SelectTrigger data-testid="rm-flag" className="rounded-sm w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All jobs</SelectItem>
              <SelectItem value="high">High RM% (≥40)</SelectItem>
              <SelectItem value="low">Low RM% (≤10)</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="no-revenue">No revenue</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-56">
          <label className="label-caps block mb-1">Search MPWO / customer / product</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="rm-search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm" placeholder="e.g. MPWO-0025, Eugia" />
          </div>
        </div>
        <Button data-testid="rm-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Apply"}
        </Button>
        <Button data-testid="rm-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <Kpi label="Jobs" value={inr(totals.jobs || 0)} />
        <Kpi label="Total revenue" value={`₹${inr(totals.revenue || 0)}`} />
        <Kpi label="Total material cost" value={`₹${inr(totals.material_cost || 0)}`} />
        <Kpi label="Weighted avg RM %" value={`${totals.avg_rm_pct || 0}%`} tone={totals.avg_rm_pct > 40 ? "danger" : totals.avg_rm_pct > 25 ? "warn" : "ok"} />
        <Kpi label="Flagged jobs (high + low)" value={inr((totals.high_flag_jobs || 0) + (totals.low_flag_jobs || 0))} tone="warn" />
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="rm-table">
          <thead className="bg-neutral-50 label-caps">
            <tr>
              <th className="text-left px-4 py-2.5">MPWO</th>
              <th className="text-left px-4 py-2.5">Customer</th>
              <th className="text-left px-4 py-2.5">Product</th>
              <th className="text-left px-4 py-2.5">Substrate</th>
              <th className="text-right px-4 py-2.5">Quantity</th>
              <th className="text-right px-4 py-2.5">Revenue</th>
              <th className="text-right px-4 py-2.5">Material Cost</th>
              <th className="text-right px-4 py-2.5">RM %</th>
              <th className="text-left px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={9} msg="No jobs match this filter." />}
            {rows.slice(0, 500).map((r) => (
              <tr key={r.mpwo_number || r.job_number} className="border-t border-neutral-100 data-row" data-testid={`rm-row-${r.mpwo_number}`}>
                <td className="px-4 py-2.5 font-mono-num text-xs"><JobLink id={r.job_id} number={r.mpwo_number || r.job_number} /></td>
                <td className="px-4 py-2.5">{r.customer_name}</td>
                <td className="px-4 py-2.5 text-xs">{r.product_name || "—"}</td>
                <td className="px-4 py-2.5 font-mono-num text-xs text-neutral-600">{r.substrate_code || "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{inr(r.quantity)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">₹{inr(r.revenue)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-neutral-600">₹{inr(r.material_cost)}</td>
                <td className={`px-4 py-2.5 text-right font-mono-num font-semibold ${
                  r.flag === "high" ? "text-rose-700" :
                  r.flag === "low" ? "text-amber-700" :
                  r.flag === "no-revenue" ? "text-neutral-400" :
                  "text-emerald-700"
                }`}>
                  {r.rm_pct}%
                  {r.flag !== "normal" && (
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">{r.flag.replace("-", " ")}</div>
                  )}
                </td>
                <td className="px-4 py-2.5 text-xs text-neutral-600">{r.status}</td>
              </tr>
            ))}
            {rows.length > 500 && (
              <tr><td colSpan={9} className="px-4 py-3 text-center text-neutral-400 text-xs">Showing first 500 of {rows.length} rows. Export CSV for full list.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// 6. Wastage per Operator / Machine
// ============================================================================
